import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Activity, ShieldCheck, RefreshCw, ShieldAlert, Shield, TrendingUp, Mail, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATE_LABELS = {
  uptime_monitor: 'Uptime-monitor',
  gsc_digest: 'GSC-digest',
  content_cadence: 'Content-concepten',
  llm_citation_check: 'LLM-citatie-check',
  security_check: 'Security-scan',
  agent_task: 'Terugkerende agent',
};

const RUN_COLORS = {
  ok: 'bg-green-100 text-green-700',
  mentioned: 'bg-green-100 text-green-700',
  draft_sent: 'bg-blue-100 text-blue-700',
  down: 'bg-red-100 text-red-700',
  not_mentioned: 'bg-amber-100 text-amber-700',
  skipped: 'bg-muted text-muted-foreground',
};

export default function OpsStatusView({ projectId }) {
  const [data, setData] = useState({ recurring_ops: [], runs: [] });
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const [leads, setLeads] = useState(null);

  const load = async () => {
    try {
      const [ops, sec, lds] = await Promise.allSettled([
        axios.get(`${API}/projects/${projectId}/ops`),
        axios.get(`${API}/projects/${projectId}/security`),
        axios.get(`${API}/projects/${projectId}/leads`),
      ]);
      if (ops.status === 'fulfilled') setData(ops.value.data);
      setSecurity(sec.status === 'fulfilled' ? sec.value.data : null);
      setLeads(lds.status === 'fulfilled' ? lds.value.data : null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const ACTION_LABELS = {
    'security-check': 'Security-scan gestart — resultaten verschijnen als action items',
    'ops-review': 'Ops-review gestart — voorstellen verschijnen in het Plan-tabblad',
    'growth-scan': 'Growth-scan gestart — toegewezen taken verschijnen in het Plan-tabblad',
    'agents/lifecycle_marketer/run': 'Mailflow-ontwerp gestart — concept komt in je mail',
  };

  const trigger = async (kind) => {
    setBusy(kind);
    try {
      await axios.post(`${API}/projects/${projectId}/${kind}`);
      toast.success(ACTION_LABELS[kind] || 'Actie gestart');
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Actie mislukt');
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const { recurring_ops: ops, runs } = data;
  const secProblems = security ? [
    ...(security.headers || []).filter((h) => !h.present).map((h) => `Ontbrekend: ${h.label}`),
    ...(security.exposed_paths || []).filter((p) => p.exposed).map((p) => `Blootgesteld: ${p.path}`),
    ...(security.tls && security.tls.checked && !security.tls.valid ? ['TLS-certificaat ongeldig/verlopen'] : []),
    ...(security.mixed_content_count > 0 ? [`${security.mixed_content_count}× mixed content`] : []),
  ] : [];
  const secSeverity = security ? (
    secProblems.some((p) => p.startsWith('Blootgesteld') || p.startsWith('TLS')) ? 'high'
      : secProblems.length > 0 ? 'medium' : 'ok'
  ) : null;

  return (
    <div className="space-y-6" data-testid="ops-status">
      <div className="flex flex-wrap gap-3 items-center">
        <Button variant="outline" size="sm" onClick={() => trigger('security-check')} disabled={busy === 'security-check'} data-testid="run-security-btn">
          {busy === 'security-check' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          Security-scan
        </Button>
        <Button variant="outline" size="sm" onClick={() => trigger('ops-review')} disabled={busy === 'ops-review'} data-testid="run-ops-review-btn">
          {busy === 'ops-review' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Ops-review
        </Button>
        <Button variant="outline" size="sm" onClick={() => trigger('growth-scan')} disabled={busy === 'growth-scan'} data-testid="run-growth-btn">
          {busy === 'growth-scan' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
          Growth-scan
        </Button>
        <Button variant="outline" size="sm" onClick={() => trigger('agents/lifecycle_marketer/run')} disabled={busy === 'agents/lifecycle_marketer/run'} data-testid="run-lifecycle-btn">
          {busy === 'agents/lifecycle_marketer/run' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          Ontwerp mailflows
        </Button>
        {leads && (
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground ml-auto">
            <Users className="w-4 h-4" /> {leads.total} leads
          </span>
        )}
      </div>

      {security && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {secSeverity === 'ok' ? <Shield className="w-4 h-4 text-green-600" /> : <ShieldAlert className="w-4 h-4 text-amber-600" />}
              Beveiliging & betrouwbaarheid
            </CardTitle>
            <CardDescription>
              Laatste scan: {new Date(security.created_at).toLocaleString('nl-NL')} · {security.domain}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {secProblems.length === 0 ? (
              <p className="text-sm text-green-700">Geen directe problemen gevonden in de laatste scan.</p>
            ) : (
              <ul className="text-sm text-foreground space-y-1 list-disc pl-5">
                {secProblems.map((p, i) => <li key={i} className={p.startsWith('Blootgesteld') || p.startsWith('TLS') ? 'text-red-600' : 'text-amber-700'}>{p}</li>)}
              </ul>
            )}
            {security.tls?.days_until_expiry != null && (
              <p className="text-xs text-muted-foreground mt-2">TLS-certificaat verloopt over {security.tls.days_until_expiry} dagen.</p>
            )}
          </CardContent>
        </Card>
      )}

      {ops.length === 0 && runs.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center text-muted-foreground">
            Nog geen terugkerende workflows. Keur terugkerende acties goed in het Plan-tabblad, of laat de Studio-orchestrator er een inplannen.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Actieve workflows</CardTitle>
          <CardDescription>Draaien via n8n, aangestuurd door laibel</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {ops.length === 0 && <p className="text-sm text-muted-foreground">Geen actieve workflows.</p>}
          {ops.map((op) => (
            <div key={op.id} className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="text-sm font-medium text-foreground">{TEMPLATE_LABELS[op.template_key] || op.template_key}</p>
                <p className="text-xs text-muted-foreground">
                  {op.last_run_at ? `Laatste run: ${new Date(op.last_run_at).toLocaleString('nl-NL')}` : 'Nog niet gedraaid'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {op.last_run_status && (
                  <Badge variant="secondary" className={RUN_COLORS[op.last_run_status] || 'bg-muted text-muted-foreground'}>
                    {op.last_run_status}
                  </Badge>
                )}
                <Badge variant="secondary" className={op.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}>
                  {op.status}
                </Badge>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recente runs</CardTitle>
          <CardDescription>Wat laibel de laatste tijd voor je deed</CardDescription>
        </CardHeader>
        <CardContent>
          {runs.length === 0 && <p className="text-sm text-muted-foreground">Nog geen runs.</p>}
          <div className="space-y-2">
            {runs.map((r) => (
              <div key={r.id} className="flex items-start justify-between py-2 border-b last:border-0">
                <div className="min-w-0">
                  <p className="text-sm text-foreground">{TEMPLATE_LABELS[r.workflow] || r.workflow}</p>
                  {r.summary && <p className="text-xs text-muted-foreground truncate max-w-md">{r.summary}</p>}
                </div>
                <div className="text-right shrink-0 ml-4">
                  <Badge variant="secondary" className={RUN_COLORS[r.status] || 'bg-muted text-muted-foreground'}>{r.status}</Badge>
                  <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString('nl-NL')}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
